import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, X } from 'lucide-react';
import { Layout } from '../components/Layout';
import { ProductCard } from '../components/ProductCard';
import { getProducts, getCategories } from '../services/api';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const sortOptions = [
  { value: 'default', label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name-asc', label: 'Name: A-Z' },
];

export const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const selectedCategory = searchParams.get('category') || '';
  const sortBy = searchParams.get('sort') || 'default';

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [productsData, categoriesData] = await Promise.all([
          getProducts(selectedCategory || null),
          getCategories(),
        ]);
        setProducts(productsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedCategory]);

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'name-asc':
        return a.name.localeCompare(b.name);
      default:
        return b.is_featured - a.is_featured;
    }
  });

  const handleCategoryChange = (category) => {
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const handleSortChange = (value) => {
    if (value === 'default') {
      searchParams.delete('sort');
    } else {
      searchParams.set('sort', value);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const currentCategory = categories.find((c) => c.id === selectedCategory);

  return (
    <Layout>
      {/* Header */}
      <section className="bg-[#F2EFE9] py-16 md:py-24" data-testid="shop-header">
        <div className="container-luxury text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3"
          >
            {currentCategory ? currentCategory.name : 'All Collections'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-5xl text-[#2C2420] mb-4"
          >
            {currentCategory ? currentCategory.name : 'Shop All'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-base text-[#5D5550] max-w-xl mx-auto"
          >
            {currentCategory
              ? currentCategory.description
              : 'Explore our complete collection of premium cashmere and wool garments.'}
          </motion.p>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="border-b border-[#EBE5DE] sticky top-20 md:top-24 bg-[#F9F8F6] z-30">
        <div className="container-luxury py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Mobile filter button */}
              <Button
                variant="outline"
                size="sm"
                className="md:hidden border-[#EBE5DE]"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="mobile-filter-btn"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>

              {/* Desktop category filters */}
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant={!selectedCategory ? 'default' : 'ghost'}
                  size="sm"
                  className={!selectedCategory ? 'bg-[#2C2420] text-[#F9F8F6]' : 'text-[#5D5550]'}
                  onClick={() => handleCategoryChange('all')}
                  data-testid="filter-all"
                >
                  All
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'ghost'}
                    size="sm"
                    className={
                      selectedCategory === category.id
                        ? 'bg-[#2C2420] text-[#F9F8F6]'
                        : 'text-[#5D5550]'
                    }
                    onClick={() => handleCategoryChange(category.id)}
                    data-testid={`filter-${category.id}`}
                  >
                    {category.name}
                  </Button>
                ))}
              </div>

              {/* Active filter badge */}
              {selectedCategory && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-xs text-[#8C8580] hover:text-[#2C2420]"
                  data-testid="clear-filters"
                >
                  <X className="h-3 w-3" />
                  Clear
                </button>
              )}
            </div>

            {/* Sort dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#8C8580] hidden sm:inline">Sort by:</span>
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger
                  className="w-[140px] sm:w-[180px] border-[#EBE5DE] text-sm"
                  data-testid="sort-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Mobile filters panel */}
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden pt-4 border-t border-[#EBE5DE] mt-4"
            >
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={!selectedCategory ? 'default' : 'ghost'}
                  size="sm"
                  className={!selectedCategory ? 'bg-[#2C2420] text-[#F9F8F6]' : 'text-[#5D5550]'}
                  onClick={() => handleCategoryChange('all')}
                >
                  All
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'ghost'}
                    size="sm"
                    className={
                      selectedCategory === category.id
                        ? 'bg-[#2C2420] text-[#F9F8F6]'
                        : 'text-[#5D5550]'
                    }
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    {category.name}
                  </Button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-spacing" data-testid="products-grid">
        <div className="container-luxury">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#5D5550] mb-4">No products found in this category.</p>
              <Button
                variant="outline"
                className="btn-secondary"
                onClick={clearFilters}
              >
                View All Products
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#8C8580] mb-8">
                Showing {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
              </p>
              <div className="product-grid">
                {sortedProducts.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};
